import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { has } from 'lodash-es';
import { t } from 'i18next';

import Drawer from '../Drawer/Drawer';
import Item from '../Dropdown/Item';

import { Styled } from './/DrawerMenu.styles';

export type DrawerMenuItem = {
  label: string
  value: string | number
  disabled?: boolean
  icon?: React.ReactNode
}

export type DrawerMenuGroup = {
  group: string,
  label?: string,
  options: DrawerMenuItem[]
}

export type DrawerMenuProps = {
  target: React.ReactElement;
  options: DrawerMenuItem[] | DrawerMenuGroup[];
  isOpen?: boolean;
  isDisabled?: boolean;
  onItemClick?: (value: any) => void;
  hoverOpensDropdown?: boolean;
};

const DrawerMenu = ({
  target,
  options,
  isOpen = false,
  isDisabled = false,
  onItemClick = undefined,
  hoverOpensDropdown = false,
}: DrawerMenuProps) => {
  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState<boolean | undefined>(false);
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const hasIcons = options.some((item) => (
    ('group' in item) ? item.options.some((item) => (has(item, 'icon'))) : has(item, 'icon')
  ));
  const hoverOpensDrawerMenuNonMobile = hoverOpensDropdown && !isMobile;

  /**
   * Toggle drawer handler.
   * @param event
   */
  const toggleDrawerMenuHandler = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!hoverOpensDrawerMenuNonMobile && !isDisabled) {
      setIsDrawerMenuOpen(!isDrawerMenuOpen);
    }
  };

  useEffect(() => {
    setIsDrawerMenuOpen(isOpen);
  }, [isOpen]);

  const handleClick = (item: any): void => {
    const { value, disabled } = item;

    setIsDrawerMenuOpen(disabled);

    if (onItemClick && !disabled) {
      onItemClick(value);
    }
  };

  return (
    <>
      {React.cloneElement(target, {
        onClick: toggleDrawerMenuHandler,
        ref: setTargetElement,
      })}
      <Drawer
        isOpen={isDrawerMenuOpen}
        position="bottom"
        title={t('action_btn_txt')}
        onClose={() => setIsDrawerMenuOpen(false)}
      >
        {
        options.map((data: any, index) => {
          if ('group' in data) {
            return (
              <Styled.DrawerMenu__Group
                key={data.group}
                isFirstGroup={index === 0}
              >
                {data?.label && (
                <Styled.DrawerMenu__Group__Name>
                    {data.label}
                </Styled.DrawerMenu__Group__Name>
                )}
                <Styled.DrawerMenu__Group__Items>
                  {data.options.map((item: any) => (
                    <Styled.DrawerMenu__Item key={item.value}>
                      <Item
                        data={item}
                        onClick={() => handleClick(item)}
                        hasIcons={hasIcons}
                      />
                    </Styled.DrawerMenu__Item>
                  ))}
                </Styled.DrawerMenu__Group__Items>
              </Styled.DrawerMenu__Group>
            );
          }
          return (
            <Styled.DrawerMenu__Item>
              <Item
                key={`${index}_${data.value}`}
                data={data}
                onClick={() => handleClick(data)}
                hasIcons={hasIcons}
              />
            </Styled.DrawerMenu__Item>
          );
        })
      }
      </Drawer>
    </>
  );
};

export default DrawerMenu;
